import React, {useContext, useEffect, useState} from "react"
import {Row, Col} from "antd"
import {FormLabel} from "components/Form"
import {BaseButton} from "components/buttons"
import {BaseInput} from "components/inputs"
import {BaseDatePicker} from "components/DateTimePicker"
import styles from "./form.module.css"
import {Auth} from "types/auth"
import {DisplayMode} from "types/common"
import cx from "classnames"
import {ActiveStudentContext} from "context/ActiveStudentContext"
import TermSelect from "components/TermSelect"
import {fieldsService, majorService, userService} from "services"
import {get, keys, pick} from "lodash"
import produce from "immer"
import {
    getFieldLabel,
    getProgramOfTermInfo,
    getStudentAcademicScoreValues,
    getTotalHourStatistics,
    handleError,
    isFieldActive,
    toDateOnlyString
} from "helpers"
import {useTranslation} from "react-i18next"
import {useModel, useStudentOverallStats} from "hooks"
import {Settings} from "types/settings"
import {useMutation, useQueryClient} from "@tanstack/react-query"
import {DepartmentCustomFields} from "./DepartmentCustomFields"

type Props = {
    onClickCancel: () => void
    onSaveSuccess: () => void
    displayMode?: DisplayMode
}

export function StudentServicesForm(props: Props) {
    const {t} = useTranslation(["common", "chat", "user"])
    const model = useModel()
    const {displayMode = "normal", onClickCancel, onSaveSuccess} = props
    const isFullDisplay = displayMode === "normal"
    const {student, setStudent} = useContext(ActiveStudentContext)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const {studentStats, programVersionTotalClockHours} = useStudentOverallStats({
        studentProfileId: student?.profileId,
        currentMajorId: student?.currentMajor?.id,
        currentMajorVersionId: student?.currentMajorVersionId
    })
    const academicScoreValues = getStudentAcademicScoreValues(studentStats)
    const [customFieldData, setCustomFieldData] = useState({})
    const queryClient = useQueryClient()

    const updateCustomFieldsForProfileMutation = useMutation(
        (data: Settings.UpdateCustomFieldsPayload) => fieldsService.updateCustomFieldsForProfile(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        "student-module-custom-fields-for-profile",
                        student.userId,
                        student.profileId,
                        "student-services"
                    ]
                })
            },
            onError: (error) => handleError(error)
        }
    )

    useEffect(() => {
        if (!student?.startingTerm) return
        ;(async function updateStartingTermDates() {
            const {data: programsOfTerm} = await majorService.getMajorByTerm([student.startingTerm.id])
            setStudent((prev) =>
                produce(prev, (draft) => {
                    const isCurrentMajorExist =
                        !!draft.currentMajor?.id && programsOfTerm.some((major) => major.id === draft.currentMajor.id)
                    const programByTerm = getProgramOfTermInfo(programsOfTerm, draft)
                    if (!!programByTerm?.startDate) {
                        draft.startDate = toDateOnlyString(programByTerm.startDate)
                    }
                    if (!!programByTerm?.projectedEndDate) {
                        draft.contractEndDate = toDateOnlyString(programByTerm.projectedEndDate)
                        draft.projectedEndDate = toDateOnlyString(programByTerm.projectedEndDate)
                    }
                    if (!isCurrentMajorExist) {
                        draft.currentMajor = null
                        draft.currentMajorVersionId = null
                    }
                })
            )
        })()
    }, [setStudent, student?.startingTerm])

    const onChangeData = React.useCallback(
        (key: string, value: any) => {
            setStudent((prev) =>
                produce(prev, (draft) => {
                    if (key === "currentMajor") {
                        draft.startingTermId = null
                        draft.startingTerm = null
                        draft.currentMajorVersionId = null
                        draft.degreeLevel = get(value, "degreeLevel.name", "")
                    }
                    draft[key] = value
                })
            )
        },
        [setStudent]
    )

    const clickSave = async () => {
        const customFieldsPayload: Settings.UpdateCustomFieldsPayload = {
            customFields: keys(customFieldData).map((key) => ({
                userId: student.userId,
                profileId: student.profileId,
                customFieldId: Number(key),
                value: customFieldData[key]
            }))
        }
        const dataSubmit: any = pick(student, [
            "startDate",
            "contractEndDate",
            "firstDateOfClass",
            "enrollmentDocsCompletionDate",
            "midpointDate",
            "projectedEndDate",
            "actualEndDate"
        ])
        dataSubmit.type = Auth.UserProfileType.Student
        dataSubmit.startingTermId = student.startingTerm ? get(student.startingTerm, "id") : null
        dataSubmit.currentTerm = student.currentTerm ? get(student.currentTerm, "id") : null
        try {
            setIsSubmitting(true)
            await Promise.all([
                customFieldsPayload.customFields.length > 0
                    ? updateCustomFieldsForProfileMutation.mutateAsync(customFieldsPayload)
                    : Promise.resolve(),
                userService.updateProfileUserById(student.profileId, dataSubmit)
            ])
            onSaveSuccess()
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const totalHours = getTotalHourStatistics(model, studentStats, programVersionTotalClockHours)
    const [totalTransferHours, completedHour, attemptedHour, remainingHour, distEdHour, _, totalMajorHours] = totalHours

    return (
        <div className={cx({[styles.wrap]: isFullDisplay})}>
            {isFullDisplay && <p className={styles.title}>Student academics Details</p>}
            <div className={styles.body}>
                <p className={styles.subTitle}>Total Progress</p>

                <Row gutter={[24, 24]}>
                    {totalTransferHours.isShow && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={totalTransferHours.title} />
                                <BaseInput readOnly value={totalTransferHours.hour} />
                            </div>
                        </Col>
                    )}
                    {attemptedHour.isShow && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={attemptedHour.title} />
                                <BaseInput readOnly value={attemptedHour.hour} />
                            </div>
                        </Col>
                    )}
                    {completedHour.isShow && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={completedHour.title} />
                                <BaseInput readOnly value={completedHour.hour} />
                            </div>
                        </Col>
                    )}
                    {remainingHour.isShow && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={remainingHour.title} />
                                <BaseInput readOnly value={remainingHour.hour} />
                            </div>
                        </Col>
                    )}
                    {distEdHour.isShow && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={distEdHour.title} />
                                <BaseInput readOnly value={distEdHour.hour} />
                            </div>
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Total hours" />
                            <BaseInput readOnly value={programVersionTotalClockHours} />
                        </div>
                    </Col>
                    {totalMajorHours.isShow && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={totalMajorHours.title} />
                                <BaseInput readOnly value={totalMajorHours.hour} />
                            </div>
                        </Col>
                    )}
                </Row>

                <p className={styles.subTitle}>General</p>

                <Row gutter={[24, 24]}>
                    {isFieldActive(model, Settings.AcademicLabel.AttendancePercentage) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel
                                    label={getFieldLabel(
                                        model,
                                        Settings.AcademicLabel.AttendancePercentage,
                                        t("user:attendancePercentage")
                                    )}
                                />
                                <BaseInput value={academicScoreValues.attendancePercentage} readOnly />
                            </div>
                        </Col>
                    )}
                    {isFieldActive(model, Settings.AcademicLabel.Gpa) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={getFieldLabel(model, Settings.AcademicLabel.Gpa, "GPA")} />
                                <BaseInput value={academicScoreValues.gpa} readOnly />
                            </div>
                        </Col>
                    )}
                    {isFieldActive(model, Settings.AcademicLabel.Cgpa) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel label={getFieldLabel(model, Settings.AcademicLabel.Cgpa, "CGPA")} />
                                <BaseInput value={academicScoreValues.cgpa} readOnly />
                            </div>
                        </Col>
                    )}
                    {isFieldActive(model, Settings.AcademicLabel.MissedAttendanceHours) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel
                                    label={getFieldLabel(
                                        model,
                                        Settings.AcademicLabel.MissedAttendanceHours,
                                        t("user:missedAttendanceHours")
                                    )}
                                />
                                <BaseInput value={academicScoreValues.missedAttendanceHours} readOnly />
                            </div>
                        </Col>
                    )}
                    {isFieldActive(model, Settings.AcademicLabel.MaxTimeframePercentage) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel
                                    label={getFieldLabel(
                                        model,
                                        Settings.AcademicLabel.MaxTimeframePercentage,
                                        "Max timeframe"
                                    )}
                                />
                                <BaseInput value={academicScoreValues.maxTimeframe} readOnly />
                            </div>
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="MPN" />
                            <BaseInput value={""} readOnly />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Entrance Counceling" />
                            <BaseInput value={""} readOnly />
                        </div>
                    </Col>
                    {isFieldActive(model, Settings.AcademicLabel.MakeUpAttendanceHours) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel
                                    label={getFieldLabel(
                                        model,
                                        Settings.AcademicLabel.MakeUpAttendanceHours,
                                        t("user:makeUpHours")
                                    )}
                                />
                                <BaseInput value={academicScoreValues.makeUpHours} readOnly />
                            </div>
                        </Col>
                    )}
                    {isFieldActive(model, Settings.AcademicLabel.HourToMakeUp) && (
                        <Col span={isFullDisplay ? 8 : 24}>
                            <div>
                                <FormLabel
                                    label={getFieldLabel(
                                        model,
                                        Settings.AcademicLabel.HourToMakeUp,
                                        "Hours to make up"
                                    )}
                                />
                                <BaseInput value={academicScoreValues.hoursToMakeup} readOnly />
                            </div>
                        </Col>
                    )}
                </Row>

                <div className={styles.divider} />

                <Row gutter={[24, 24]}>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label={t("user:user.currentTerm")} />
                            <TermSelect
                                value={student.currentTerm}
                                onChange={(newValue) => onChangeData("currentTerm", newValue)}
                                majorId={get(student.currentMajor, "id") as number}
                                majorVersionId={get(student.currentMajorVersionId, "id") as number}
                                campusIds={student.campusIds}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label={t("user:user.startTerm")} />
                            <TermSelect
                                isClearable
                                value={student.startingTerm}
                                onChange={(newValue) => onChangeData("startingTerm", newValue)}
                            />
                        </div>
                    </Col>

                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(model, Settings.GeneralLabel.StartDate, t("user:user.startDate"))}
                            />
                            <BaseDatePicker value={student.startDate || null} dateOnly readOnly />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.ContractEndDate,
                                    t("user:user.contractEndDate")
                                )}
                            />
                            <BaseDatePicker value={student.contractEndDate || null} dateOnly readOnly />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.FirstDateOfClass,
                                    t("user:user.firstDateOfClass")
                                )}
                            />
                            <BaseDatePicker
                                value={student.firstDateOfClass || null}
                                dateOnly
                                onChange={(date) => onChangeData("firstDateOfClass", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.EnrollmentDocsCompletionDate,
                                    t("user:user.enrollmentDocsCompletionDate")
                                )}
                            />
                            <BaseDatePicker
                                value={student.enrollmentDocsCompletionDate || null}
                                dateOnly
                                onChange={(date) => onChangeData("enrollmentDocsCompletionDate", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.MidpointDate,
                                    t("user:user.midpointDate")
                                )}
                            />
                            <BaseDatePicker
                                value={student.midpointDate || null}
                                dateOnly
                                onChange={(date) => onChangeData("midpointDate", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.ProjectedEndDate,
                                    t("user:user.projectedEndDate")
                                )}
                            />
                            <BaseDatePicker
                                value={student.projectedEndDate || null}
                                dateOnly
                                onChange={(date) => onChangeData("projectedEndDate", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={
                                    getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.ActualEndDate,
                                        t("user:user.actualEndDate")
                                    ) + " (System generated)"
                                }
                            />
                            <BaseDatePicker value={student.actualEndDate || null} dateOnly readOnly />
                        </div>
                    </Col>

                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="LDA" />
                            <BaseDatePicker value={student.lastDayOfAttendance || null} dateOnly readOnly />
                        </div>
                    </Col>
                </Row>
                <div className={styles.divider} />
                <DepartmentCustomFields
                    module={Settings.CustomFieldModule.StudentServices}
                    userId={student.userId}
                    profileId={student.profileId}
                    isFullDisplay={isFullDisplay}
                    customFieldData={customFieldData}
                    setCustomFieldData={setCustomFieldData}
                    onChange={(customFieldId, value) =>
                        setCustomFieldData((prev) => ({...prev, [customFieldId]: value}))
                    }
                />
            </div>

            <div className={styles.action}>
                <BaseButton title="Cancel" variant="secondary" onClick={onClickCancel} />
                <BaseButton title="Save" onClick={clickSave} loading={isSubmitting} />
            </div>
        </div>
    )
}
